/**
 * @file Lightweight event bus for cross-feature communication
 *
 * Replaces direct global function calls between features with a decoupled
 * pub/sub pattern. No dependencies. Namespace events with colons:
 *   emit('job:created', job)
 *   on('invoice:sent', handler)
 */

/** @type {Map<string, Set<Function>>} */
const listeners = new Map();

/**
 * Subscribe to an event.
 * @param {string} event - Event name (e.g. 'job:created')
 * @param {Function} callback - Handler function
 */
export function on(event, callback) {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event).add(callback);
}

/**
 * Unsubscribe from an event.
 * @param {string} event - Event name
 * @param {Function} callback - The same function reference passed to on()
 */
export function off(event, callback) {
  const set = listeners.get(event);
  if (set) {
    set.delete(callback);
    if (set.size === 0) listeners.delete(event);
  }
}

/**
 * Fire an event, calling all subscribers with the payload.
 * @param {string} event - Event name
 * @param {*} [payload] - Data to pass to subscribers
 */
export function emit(event, payload) {
  const set = listeners.get(event);
  if (!set) return;
  for (const cb of set) {
    try {
      cb(payload);
    } catch (err) {
      console.error(`[events] Error in listener for "${event}":`, err);
    }
  }
}

/**
 * Subscribe to an event for a single firing only.
 * @param {string} event - Event name
 * @param {Function} callback - Handler function (called once then removed)
 */
export function once(event, callback) {
  const wrapper = (payload) => {
    off(event, wrapper);
    callback(payload);
  };
  on(event, wrapper);
}
